import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  TagIcon, 
  ClipboardDocumentListIcon, 
  TruckIcon, 
  ArchiveBoxIcon, 
  ChartBarIcon,
  ArrowDownOnSquareIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { message } from 'antd';

// Importation des composants
import ProductList from '../components/products/ProductList';
import ProductForm from '../components/products/ProductForm';
import StockManagement from '../components/products/StockManagement';
import InventoryAnalytics from '../components/products/InventoryAnalytics';
import SupplyManagement from '../components/products/SupplyManagement';
import InventoryManagement from '../components/products/InventoryManagement';
import ShopifyImport from '../components/products/ShopifyImport';
import PriceAdjustmentModal from '../components/products/PriceAdjustmentModal';
import PlanGate from '../components/common/PlanGate';
import { productsApi } from '../utils/api';
import usePlan from '../hooks/usePlan';
// Indicateur d'activation de l'intégration Shopify (REACT_APP_ENABLE_SHOPIFY)
import { SHOPIFY_ENABLED } from '../config/features';

const Products = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('catalogue');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showShopifyImport, setShowShopifyImport] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { canCreate } = usePlan();

  // Charger les produits au montage
  // useEffect(() => {
  //   loadProducts();
  // }, []);

  /**
   * Charger la liste des produits
   */
  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productsApi.getAll();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      message.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = (product) => {
    // Dans une application réelle, ici on enverrait les données à l'API
    console.log('Produit sauvegardé:', product);
    setShowProductForm(false);
    setEditingProduct(null);
    // Recharger la liste des produits
    loadProducts();
  };

  /**
   * Gérer la création d'un nouveau produit avec vérification des limites
   */
  const handleNewProduct = async () => {
    try {
      // Vérifier les limites du plan
      const limitCheck = await canCreate('products', products.length);
      
      if (!limitCheck.allowed) {
        message.warning(
          `Limite atteinte: ${limitCheck.current}/${limitCheck.limit} produits autorisés sur votre plan.`
        );
        return;
      }

      // Naviguer vers la page de création de produit
      navigate('/app/products/new');
    } catch (error) {
      console.error('Erreur lors de la vérification des limites:', error);
      message.error('Erreur lors de la vérification des limites');
    }
  };

  /**
   * Gérer l'ajustement de prix en masse
   */
  const handlePriceAdjustment = async (data) => {
    try {
      const response = await productsApi.adjustPrice(data);
      if (response.data.success) {
        message.success(response.data.message);
        setShowPriceModal(false);
        // Rafraîchir la liste si elle est chargée (actuellement commentée dans le code d'origine, mais dispo via reload)
        // loadProducts(); 
        // Force refresh via navigation or context reload if needed, 
        // but since list is handled by ProductList internal state mostly, we might want to signal it to refresh.
        // For now, message success is enough.
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajustement des prix:', error);
      message.error(error.response?.data?.message || 'Erreur lors de l\'ajustement des prix');
    }
  };

  /**
   * Gérer l'import Shopify avec vérification des limites
   */
  const handleShopifyImport = async () => {
    try {
      // Vérifier les limites du plan
      const limitCheck = await canCreate('products', products.length);
      
      if (!limitCheck.allowed) {
        message.warning(
          `Vous ne pouvez pas importer de nouveaux produits. Limite atteinte: ${limitCheck.current}/${limitCheck.limit} produits.`
        );
        return;
      }

      setShowShopifyImport(true);
    } catch (error) {
      console.error('Erreur lors de la vérification des limites:', error);
      message.error('Erreur lors de la vérification des limites');
    }
  };

  const handleShopifyImportComplete = (importedProducts, count) => {
    // Dans une application réelle, ici on enverrait les données à l'API pour ajouter les produits
    console.log(`${count} produits importés depuis Shopify:`, importedProducts);
    message.success(`${count} produits importés avec succès`);
    // Recharger la liste des produits
    loadProducts();
  };

  const tabs = [
    { name: 'Catalogue', value: 'catalogue', icon: TagIcon },
    { name: 'Stocks', value: 'stocks', icon: ArchiveBoxIcon },
    // { name: 'Approvisionnement', value: 'approvisionnement', icon: TruckIcon },
    // { name: 'Inventaire', value: 'inventaire', icon: ClipboardDocumentListIcon },
    // { name: 'Analyse', value: 'analyse', icon: ChartBarIcon },
  ];

  return (
    <div className="w-full p-3 sm:p-6">
      {/* Header responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className={`${window.innerWidth < 768 ? 'text-lg' : 'text-2xl'} font-semibold text-gray-900`}>
          <span className="hidden sm:inline">Gestion des </span>Produits
        </h1>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          
          {/* Bouton Ajuster les prix */}
          <button
            type="button"
            onClick={() => setShowPriceModal(true)}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full sm:w-auto"
          >
            <CurrencyDollarIcon className="-ml-1 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5 text-gray-500" aria-hidden="true" />
            <span className="hidden sm:inline">Ajuster les prix</span>
            <span className="sm:hidden">Prix</span>
          </button>

          {/* Import Shopify avec contrôle de plan - Version responsive.
              Masqué quand l'intégration Shopify est désactivée (REACT_APP_ENABLE_SHOPIFY). */}
          {SHOPIFY_ENABLED && <PlanGate
            feature="shopify_import"
            type="feature"
            fallback={
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed w-full sm:w-auto"
              >
                <ArrowDownOnSquareIcon className="-ml-1 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                <span className="hidden sm:inline">Import Shopify (Plan requis)</span>
                <span className="sm:hidden">Import Shopify</span>
              </button>
            }
          >
            <button
              type="button"
              onClick={handleShopifyImport}
              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full sm:w-auto"
            >
              <ArrowDownOnSquareIcon className="-ml-1 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5 text-gray-500" aria-hidden="true" />
              <span className="hidden sm:inline">Importer depuis Shopify</span>
              <span className="sm:hidden">Import Shopify</span>
            </button>
          </PlanGate>}

          {/* Action de création mise en avant en citron ; les limites et le clic restent identiques. */}
          <PlanGate 
            type="limit" 
            limitType="products" 
            currentCount={products.length}
            fallback={
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-gray-400 bg-gray-200 cursor-not-allowed w-full sm:w-auto"
              >
                <PlusIcon className="-ml-1 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
                <span className="hidden sm:inline">Limite atteinte</span>
                <span className="sm:hidden">Limite</span>
              </button>
            }
          >
            <button
              type="button"
              onClick={handleNewProduct}
              className="private-button-accent inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-xs sm:text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full sm:w-auto"
            >
              <PlusIcon className="-ml-1 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5" aria-hidden="true" />
              <span className="hidden sm:inline">Nouveau Produit</span>
              <span className="sm:hidden">Nouveau</span>
            </button>
          </PlanGate>
        </div>
      </div>

      {/* Formulaire de produit (modal) - Responsive */}
      {showProductForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowProductForm(false)}></div>
            <div className={`relative bg-white rounded-lg shadow-xl w-full ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-hidden`}>
              <ProductForm 
                product={editingProduct}
                onClose={() => setShowProductForm(false)}
                onSave={handleSaveProduct}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal d'importation Shopify - Responsive */}
      {showShopifyImport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowShopifyImport(false)}></div>
            <div className={`relative bg-white rounded-lg shadow-xl w-full ${window.innerWidth < 768 ? 'max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-hidden`}>
              <ShopifyImport
                onClose={() => setShowShopifyImport(false)}
                onImportComplete={handleShopifyImportComplete}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajustement de prix */}
      <PriceAdjustmentModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        onConfirm={handlePriceAdjustment}
      />

      {/* Onglets responsive */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`
                ${activeTab === tab.value
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                group inline-flex items-center py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0
              `}
            >
              <tab.icon
                className={`
                  ${activeTab === tab.value ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                  -ml-0.5 mr-1 sm:mr-2 h-4 sm:h-5 w-4 sm:w-5
                `}
                aria-hidden="true"
              />
              <span className="hidden sm:inline">{tab.name}</span>
              <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu des onglets - Version responsive */}
      <div className="mt-4 sm:mt-6">
        {activeTab === 'catalogue' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-3 py-4 sm:px-6 sm:py-5">
              <h3 className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} leading-6 font-medium text-gray-900`}>
                Catalogue de Produits
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                Gérez vos produits, catégories, variations, prix et descriptions.
              </p>
              <div className="mt-4 sm:mt-6">
                <ProductList 
                  // products={products} 
                  // loading={loading}
                  // onProductUpdate={loadProducts}
                /> 
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stocks' && (
          <PlanGate 
            feature="inventory_sync" 
            type="feature"
            fallback={null}
          >
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-3 py-4 sm:px-6 sm:py-5">
                <h3 className={`${window.innerWidth < 768 ? 'text-base' : 'text-lg'} leading-6 font-medium text-gray-900`}>
                  Gestion des Stocks
                </h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-500">
                  Suivez vos stocks disponibles, mouvements et configurez les seuils d'alerte.
                </p>
                <div className="mt-4 sm:mt-6">
                   <StockManagement /> 
                </div>
              </div>
            </div>
          </PlanGate>
        )}

        {activeTab === 'approvisionnement' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Approvisionnement</h3>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos fournisseurs, bons de commande et réceptions de marchandises.
              </p>
              <div className="mt-6">
                <SupplyManagement />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'inventaire' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Inventaire</h3>
              <p className="mt-1 text-sm text-gray-500">
                Planifiez et réalisez vos inventaires, réconciliez les écarts.
              </p>
              <div className="mt-6">
                <InventoryManagement />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analyse' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Analyse et Reporting</h3>
              <p className="mt-1 text-sm text-gray-500">
                Consultez les statistiques de rotation des stocks, valorisation et analyses de performance.
              </p>
              <div className="mt-6">
                <InventoryAnalytics />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products; 